import { Collapse, IconButton, makeStyles, Theme } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import MuiAlert from '@material-ui/lab/Alert';
import MuiAlertTitle from '@material-ui/lab/AlertTitle';
import React, { forwardRef } from 'react';
import colors from '../../../src/themes/main-colors';
import { useIsMobile } from '../../util/style-utils';
import clsx from 'clsx';

import { OutageMapNotificationContext } from '../../providers/OutageMapNotificationProvider';
interface OutageMapNotificationProps {
  severity?: NotificationSeverity;
  variant?: NotificationVariant;
  title?: string;
  message?: string;
  isOpen?: boolean;
}

export type NotificationSeverity = 'error' | 'warning' | 'info' | 'success';
export type NotificationVariant = 'filled' | 'outlined' | 'standard';
type AlertClasses = {
  success: string;
  error: string;
  warning: string;
  info: string;
};

const useStyles = makeStyles((theme: Theme) => ({
  collapseWrapper: {
    '& .MuiCollapse-wrapperInner': {
      margin: '1rem',
      [theme.breakpoints.down('sm')]: {
        margin: '1rem 0.8rem',
      },
    },
  },
  outageMapNotification: {
    width: '18.5rem',
    padding: '8px 15px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  closeBtn: {
    width: '1rem',
    height: '1rem',
    '& .MuiSvgIcon-root': {
      width: '1rem',
      height: '1rem',
    },
  },
  alert: {
    '& > .MuiAlert-message > p': {
      margin: 0,
    },
  },
  message: {
    fontSize: '1rem',
    fontWeight: 700,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      fontWeight: 700,
      paddingTop: '6px',
    },
  },
  filledSuccess: {
    backgroundColor: colors.shoreGreen,
  },
  filledWarning: {
    backgroundColor: colors.warning,
  },
  filledError: {
    backgroundColor: colors.roseRed,
    '& a': {
      color: '#FFF',
      textDecoration: 'none',
      fontWeight: 'bold',
    },
  },
  filledInfo: {
    backgroundColor: colors.info,
  },
  successIcon: {
    '& path': {
      transform: 'scale(1.25) translateX(-2.5px) translateY(-2.5px)',
    },
    fill: colors.white,
    fontSize: 'inherit',
    borderRadius: '50%',
  },
  errorIcon: {
    '& path': {
      transform: 'scale(1.25) translateX(-2.5px) translateY(-2.5px)',
    },
    fill: colors.darkRed,
    fontSize: 'inherit',
    background: colors.white,
    borderRadius: '50%',
    transform: 'scale(1.2)',
  },
  warningIcon: {
    fill: colors.white,
    fontSize: 'inherit',
    background: colors.orange,
    transform: 'scale(1.3)',
  },
  infoIcon: {
    '& path': {
      transform: 'scale(1.25) translateX(-2.5px) translateY(-2.5px)',
    },
    fill: colors.darkBlue,
    fontSize: 'inherit',
    background: colors.white,
    borderRadius: '50%',
    transform: 'scale(1.2)',
  },
}));

const OutageMapNotifications = forwardRef<
  HTMLElement,
  OutageMapNotificationProps
>((props, ref) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const alertClasses: AlertClasses = {
    success: classes.filledSuccess,
    error: classes.filledError,
    warning: classes.filledWarning,
    info: classes.filledInfo,
  };
  return (
    <OutageMapNotificationContext.Consumer>
      {({ state, setState }) => {
        const outageNotification = props.isOpen ? props : state;
        const notificationSeverity: NotificationSeverity =
          outageNotification.severity;
        const alertClass =
          alertClasses[notificationSeverity] || alertClasses['info'];

        return (
          <Collapse
            in={outageNotification.isOpen}
            style={{ zIndex: 0 }}
            className={classes.collapseWrapper}
          >
            <MuiAlert
              className={clsx(
                alertClass,
                classes.outageMapNotification,
                classes.message,
                classes.alert,
              )}
              ref={ref}
              severity={outageNotification.severity}
              variant={outageNotification.variant}
              iconMapping={{
                success: <CheckCircleIcon className={classes.successIcon} />,
                error: <ErrorIcon className={classes.errorIcon} />,
                warning: <ErrorIcon className={classes.warningIcon} />,
                info: <InfoIcon className={classes.infoIcon} />,
              }}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size={isMobile ? 'small' : 'medium'}
                  onClick={() => setState({ isOpen: false })}
                  className={classes.closeBtn}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {outageNotification.title && (
                <MuiAlertTitle>{outageNotification.title}</MuiAlertTitle>
              )}
              {outageNotification.message}
            </MuiAlert>
          </Collapse>
        );
      }}
    </OutageMapNotificationContext.Consumer>
  );
});

export default OutageMapNotifications;
